// Pure selection model for the editable WHERE Rail wrap gesture (Slice 3B.5).
// A selection is a contiguous run [from, to] of sibling indices within ONE
// group (identified by its path). Plain click sets a single-element anchor;
// shift-click within the same group extends the run from the stored anchor.

export type RailSelection = {
  readonly parentPath: readonly number[];
  readonly anchor: number;
  readonly from: number;
  readonly to: number;
} | null;

const keyOf = (path: readonly number[]): string => path.join('-') || 'root';

/**
 * Update the selection after a click on child `index` of the group at
 * `parentPath`. Shift-click in the same group extends the run [min, max] from
 * the existing anchor; any other click (plain, or in a different group) starts
 * a fresh single-element selection anchored at `index`.
 */
export function toggleSelection(
  prev: RailSelection,
  parentPath: readonly number[],
  index: number,
  shift: boolean,
): RailSelection {
  if (shift && prev && keyOf(prev.parentPath) === keyOf(parentPath)) {
    return {
      parentPath,
      anchor: prev.anchor,
      from: Math.min(prev.anchor, index),
      to: Math.max(prev.anchor, index),
    };
  }
  return { parentPath, anchor: index, from: index, to: index };
}

/** True when child `index` of the group at `parentPath` is in the selection. */
export function isChildSelected(
  sel: RailSelection,
  parentPath: readonly number[],
  index: number,
): boolean {
  if (!sel || keyOf(sel.parentPath) !== keyOf(parentPath)) return false;
  return index >= sel.from && index <= sel.to;
}

/** True when the selection spans ≥2 contiguous siblings (worth wrapping). */
export function isWrappable(sel: RailSelection): boolean {
  return !!sel && sel.to > sel.from;
}
